import React from 'react';
import PageHero from '../components/PageHero';
import { heroImages, brand } from '../mock';
import { CheckCircle2, Target, Users, Award, Heart, Sparkles, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const values = [
  { icon: Target, title: 'Result-Driven', desc: 'Every strategy is measured against real outcomes — sales, growth, ROI.' },
  { icon: Users, title: 'Client-First', desc: 'Dedicated managers who treat your brand as their own.' },
  { icon: Award, title: 'Certified Experts', desc: 'Amazon, Flipkart & Meta certified professionals on your team.' },
  { icon: Heart, title: 'Transparent', desc: 'Clear reporting, honest pricing, no hidden surprises.' },
];

const timeline = [
  { year: '2015', text: 'Founded with a mission to help Indian sellers scale online.' },
  { year: '2018', text: 'Expanded to Flipkart, Meesho and international marketplaces.' },
  { year: '2021', text: 'Added Social Media Marketing and Website Development divisions.' },
  { year: '2024', text: 'Serving 30,000+ merchants across 8+ marketplaces.' },
];

export default function AboutPage() {
  return (
    <>
      <PageHero
        eyebrow="About Us"
        title="We Grow Brands That Deserve to Be Seen"
        sub="EC SAFETRADE SOLUTIONS is a full-service digital growth partner helping merchants scale across marketplaces, social media and their own websites."
        image={heroImages.team}
      />

      <section className="section bg-white">
        <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src={heroImages.team2} alt="office" className="rounded-3xl w-full h-[420px] object-cover shadow-xl" />
            <img src={heroImages.warehouse} alt="warehouse" className="hidden md:block absolute -bottom-8 -right-8 w-52 h-52 object-cover rounded-2xl shadow-xl border-4 border-white" />
          </div>
          <div>
            <div className="eyebrow">Our Story</div>
            <h2 className="section-title mt-3">From a small team to a <span className="grad-text">growth powerhouse</span></h2>
            <p className="section-sub mt-5">
              What started as a passion project to help small sellers navigate Amazon has grown
              into a full-fledged agency managing thousands of listings, campaigns and websites
              across the globe. We combine data, creativity and hands-on execution to deliver
              measurable growth.
            </p>
            <div className="mt-6 space-y-3">
              {['Certified marketplace experts', 'Dedicated account managers', 'Weekly performance reviews', 'End-to-end execution'].map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-[color:var(--ink)] font-medium">
                  <CheckCircle2 size={18} className="text-[color:var(--brand-green)]" /> {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-[color:var(--brand-green-light)]/40">
        <div className="container-x">
          <div className="text-center mb-12">
            <div className="eyebrow">Our Values</div>
            <h2 className="section-title mt-3">What We Stand For</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div key={v.title} className="card-hover bg-white rounded-2xl p-6 shadow-sm border border-neutral-100 relative overflow-hidden">
                <div className="absolute -right-6 -top-6 w-20 h-20 rounded-full opacity-10" style={{ background: i % 2 ? 'var(--brand-orange)' : 'var(--brand-green)' }} />
                <div className="w-14 h-14 rounded-2xl bg-[color:var(--brand-green-light)] flex items-center justify-center text-[color:var(--brand-green)] mb-5">
                  <v.icon size={26} />
                </div>
                <h3 className="font-bold text-[color:var(--ink)] text-lg">{v.title}</h3>
                <p className="text-sm text-[color:var(--ink-soft)] mt-2 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-x max-w-4xl">
          <div className="text-center mb-12">
            <div className="eyebrow">Milestones</div>
            <h2 className="section-title mt-3">Our Journey So Far</h2>
          </div>
          <div className="relative border-l-4 border-[color:var(--brand-green)]/20 ml-4 space-y-8 pl-8">
            {timeline.map((t) => (
              <div key={t.year} className="relative">
                <div className="absolute -left-[46px] w-8 h-8 rounded-full bg-[color:var(--brand-green)] text-white flex items-center justify-center shadow-lg">
                  <Sparkles size={16} />
                </div>
                <div className="bg-[color:var(--brand-green-light)]/50 rounded-2xl p-5 border border-[color:var(--brand-green)]/10">
                  <div className="text-[color:var(--brand-orange)] font-black text-xl">{t.year}</div>
                  <p className="text-[color:var(--ink)] mt-1">{t.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-r from-[color:var(--brand-green)] to-[color:var(--brand-green-dark)] text-white text-center">
        <div className="container-x">
          <h2 className="text-3xl md:text-4xl font-black">Ready to grow with us?</h2>
          <p className="mt-3 text-white/85">Talk to our experts and get a free growth audit today.</p>
          <Link to="/contact" className="btn-orange mt-6 inline-flex">Book Free Consultation</Link>
          <a
            href={`https://wa.me/${brand.whatsapp}?text=${encodeURIComponent('Hi EC Safetrade, I would like to book a FREE consultation. Please share more details.')}`}
            target="_blank"
            rel="noreferrer"
            className="mt-6 ml-3 inline-flex items-center gap-2 font-semibold px-6 py-3 rounded-full border-2 border-white text-white hover:bg-white/10 transition"
          >
            <MessageCircle size={16} /> Or WhatsApp us
          </a>
        </div>
      </section>
    </>
  );
}
